import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import {
  BID_LINES_IMPORT_ERROR_MESSAGE,
  BID_LINES_IMPORT_SUCCESS,
  BID_LINES_REIMPORT_CONFIRMATION_MESSAGE,
  SUCCESS_TITLE,
} from "../../util/messageConstants";
import React, { useState, useRef, useEffect } from "react";
import {
  getBidLineColumns,
  getBlockLinesCount,
  getEmployeeErrorsCount,
  validateAndTransformBidFileToJson,
  sendBidLineImportFileToS3,
  isAssignmentUsedToDisplay,
} from "../../util/bid/shiftBidUtils";
import {
  getEmployeeFtCount,
  getEmployeePtCount,
  isDeletedBid,
  isOpenBid,
  isClosedBid,
  isAwardedBid,
} from "../../util/bid/bidCommon";

import { getEligibleEmployees, getIneligibleEmployees, getEmployeeUnrankedCount } from "../../util/bid/bidGroupUtils";
import { BID_LINES_PAGE_TYPE, NON_CONTRACT } from "../../util/constants";
import { format_DOW_ShortMonth_Date_Year } from "../../util/dateTimeFormatter";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SuccessDialog from "../fundamentals/SuccessDialog";
import WarningDialog from "../fundamentals/WarningDialog";
import EmployeeBlockLineDetailPanelContent from "./EmployeeBlockLineDetailPanelContent";
import ShiftBidPDFExport from "../../pages/bid/ShiftBidPDFExport";
const useStyles = makeStyles((theme) => ({
  bidLinesTable: {
    border: 0,
    "& div": {
      "& .MuiDataGrid-footerContainer": {
        display: "none",
      },
    },
    borderRadius: "0px",
  },
  buttonStyle: {
    background: theme.palette.secondary.swaWhite,
  },
}));

const CreateBidLines = (props) => {
  const classes = useStyles();
  const [isBidLinesClearAllDialogOpen, setIsBidLinesClearAllDialogOpen] = useState(false);
  const [isBidLinesImportConfirmationDialogOpen, setIsBidLinesImportConfirmationDialogOpen] = useState(false);
  const [isBidLineImportErrorDialogOpen, setIsBidLineImportErrorDialogOpen] = useState(false);
  const [isBidLineImportSuccessDialogOpen, setIsBidLineImportSuccessDialogOpen] = useState(false);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = React.useState([]);

  const inputRef = useRef();

  useEffect(() => {
    const clickedBidlineIds = props.bidLines.filter((bidline) => bidline.isExpanded).map((bidline) => bidline.id);
    setDetailPanelExpandedRowIds(clickedBidlineIds);
  }, [props.bidLines]);

  const handleBlockLineClick = (id) => {
    const copiedBidLines = [...props.bidLines];
    const clickedBidlineIdx = copiedBidLines.findIndex((bidline) => bidline.id === id);
    copiedBidLines[clickedBidlineIdx].isBlockLine = !copiedBidLines[clickedBidlineIdx].isBlockLine;
    copiedBidLines[clickedBidlineIdx].isExpanded = copiedBidLines[clickedBidlineIdx].isBlockLine;
    copiedBidLines[clickedBidlineIdx].assignedEmployee = null;
    props.setBidLines(copiedBidLines);
  };

  const handleDetailPanelExpandedRowIdsChange = React.useCallback((newIds) => {
    setDetailPanelExpandedRowIds(newIds);
  }, []);

  const handleLoadCSVClick = (event) => {
    // if bid lines are already loaded, show a confirmation dialog to re-import, else open file browser
    if (props.bidLines && props.bidLines.length > 0) {
      setIsBidLinesImportConfirmationDialogOpen(true);
    } else {
      inputRef.current.click();
    }
  };

  const handleBidFileUploaded = (event) => {
    if (event.target.files.length > 0) {
      const newlyUploadedBidFile = event.target.files[0];

      // Check if the selected file is a CSV
      if (newlyUploadedBidFile.type !== "text/csv") {
        setIsBidLineImportErrorDialogOpen(true);
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", async (event) => {
        try {
          await sendBidLineImportFileToS3(
            props.bid.station,
            props.bid.referenceNumber,
            newlyUploadedBidFile.name,
            event.target.result
          );
          const contract = getUnionContractGivenWorkgroup(props.bid.workgroup);
          const bidLineJson = await validateAndTransformBidFileToJson(event.target.result, contract);
          props.setBidLines(bidLineJson.bidLines);
          setIsBidLineImportSuccessDialogOpen(true);
        } catch (error) {
          console.log("Error: ", error);
          setIsBidLineImportErrorDialogOpen(true);
          return;
        }
      });
      reader.readAsText(newlyUploadedBidFile);
    }
  };

  const handleBidLineImportConfirmationDialogOkClick = () => {
    setIsBidLinesImportConfirmationDialogOpen(false);
    inputRef.current.value = "";
    inputRef.current.click();
  };

  const handleBidLineImportConfirmationDialogClose = () => {
    setIsBidLinesImportConfirmationDialogOpen(false);
  };

  const handleBidLineImportErrorDialogClose = () => {
    inputRef.current.value = "";
    setIsBidLineImportErrorDialogOpen(false);
  };

  const handleBidLinesClearAll = (event) => {
    props.setBidLines([]);
    setIsBidLinesClearAllDialogOpen(false);
    let fileInput = document.getElementById("loadCsvInput");
    fileInput.value = "";
  };

  const getDetailPanelContent = React.useCallback(
    ({ row }) =>
      row.isExpanded && (
        <EmployeeBlockLineDetailPanelContent
          bidLineRow={row}
          bidLines={props.bidLines}
          bidGroup={props.bidGroup}
          setBidLines={props.setBidLines}
          bidStatus={props.bid.status}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.bidLines]
  );

  const getDetailPanelHeight = React.useCallback(({ row }) => (row.isExpanded ? "auto" : 0), []);

  return (
    <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
      {/* Station | Workgroup & Export/Print buttons */}
      <Grid container sx={{ padding: "10px" }}>
        <Grid item xs={10}>
          <Typography
            variant="h6"
            sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
          >
            {props.bid.station} | {props.bid.workgroup}{" "}
            {props.schedDates[0] !== null &&
              props.schedDates[1] !== null &&
              `| ${format_DOW_ShortMonth_Date_Year(props.schedDates[0])} - ${format_DOW_ShortMonth_Date_Year(
                props.schedDates[1]
              )}`}
            {props.bidName && ` | ${props.bidName}`}
          </Typography>
        </Grid>
        <Grid item xs={2} justifyContent="flex-end">
          <ShiftBidPDFExport
            bidLines={props.bidLines}
            bidChoiceData={props.bid}
            isAdminBidLineExport={true}
            useAssignmentForEmp={isAssignmentUsedToDisplay(props.bid)}
          />
        </Grid>
      </Grid>

      {/* Load CSV & Clear All/Add Bid Line/Sort By buttons */}
      <Grid container sx={{ bgcolor: "secondary.swaLightestBlue", padding: "10px" }} alignItems={"center"}>
        <Grid item xs={2} sx={{ textAlign: "left" }}>
          <Box sx={{ paddingLeft: "10px" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              component="label"
              onClick={handleLoadCSVClick}
              disabled={
                isDeletedBid(props.bid.status) ||
                isOpenBid(props.bid.status) ||
                isClosedBid(props.bid.status) ||
                isAwardedBid(props.bid.status)
              }
            >
              LOAD BID LINES
            </Button>

            <ConfirmationDialog
              isDialogOpen={isBidLinesImportConfirmationDialogOpen}
              title="Re-loading the Bid Lines"
              message={BID_LINES_REIMPORT_CONFIRMATION_MESSAGE}
              handleYesButtonClicked={handleBidLineImportConfirmationDialogOkClick}
              handleNoButtonClicked={handleBidLineImportConfirmationDialogClose}
            />
          </Box>
        </Grid>
        <Grid item xs={8} sx={{ display: "flex", justifyContent: "left" }}>
          <Box sx={{ padding: "5px" }}>
            <Grid container>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Bid Lines
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    Total:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {props.bidLines.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    FT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeeFtCount(props.bidLines)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    PT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeePtCount(props.bidLines)}
                    </Typography>
                  </Grid>
                  {getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT && (
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      Block Lines:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getBlockLinesCount(props.bidLines)}
                      </Typography>
                    </Grid>
                  )}
                  {getEmployeeErrorsCount(props.bidLines) > 0 && (
                    <Grid item xs={2} sx={{ textAlign: "left", color: "primary.swaRed" }}>
                      Errors:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeeErrorsCount(props.bidLines)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Eligible
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    Total:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEligibleEmployees(props.bidGroup).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    FT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeeFtCount(getEligibleEmployees(props.bidGroup))}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    PT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeePtCount(getEligibleEmployees(props.bidGroup))}
                    </Typography>
                  </Grid>
                  <Grid item xs={3} sx={{ textAlign: "left" }}>
                    Unranked:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeeUnrankedCount(getEligibleEmployees(props.bidGroup))}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Ineligible
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    Total:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getIneligibleEmployees(props.bidGroup).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    FT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeeFtCount(getIneligibleEmployees(props.bidGroup))}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left" }}>
                    PT:{" "}
                    <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {getEmployeePtCount(getIneligibleEmployees(props.bidGroup))}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}></Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={2} sx={{ display: "flex", justifyContent: "right" }}>
          <Box sx={{ paddingRight: "10px" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setIsBidLinesClearAllDialogOpen(true);
              }}
              className={classes.buttonStyle}
              size="small"
              disabled={
                isDeletedBid(props.bid.status) ||
                isOpenBid(props.bid.status) ||
                isClosedBid(props.bid.status) ||
                isAwardedBid(props.bid.status)
              }
            >
              Clear All
            </Button>
            <WarningDialog
              isDialogOpen={isBidLineImportErrorDialogOpen}
              handleClickDialogClose={handleBidLineImportErrorDialogClose}
              header="Unable to Import"
              message={BID_LINES_IMPORT_ERROR_MESSAGE}
              severity={"error"}
            />
            <SuccessDialog
              isSuccessDialogOpen={isBidLineImportSuccessDialogOpen}
              title={SUCCESS_TITLE}
              message={BID_LINES_IMPORT_SUCCESS}
              handleOKClick={() => setIsBidLineImportSuccessDialogOpen(false)}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Bid Line Table */}
      <Grid container>
        <Box
          sx={{
            height: `calc(85vh - 315px)`,
            width: "inherit",
            "& .gray-color-cell": {
              backgroundColor: "secondary.swaGray3",
            },
          }}
          data-testid="bid-lines-grid"
        >
          <DataGridPro
            className={classes.bidLinesTable}
            columns={getBidLineColumns(
              { contract: getUnionContractGivenWorkgroup(props.bid.workgroup) },
              handleBlockLineClick,
              props.bid.status
            )}
            rows={props.bidLines}
            rowHeight={60}
            getRowHeight={() => "auto"}
            components={{
              LoadingOverlay: customLoadingTableOverlay,
              NoRowsOverlay: CustomNoRowsTableOverlay,
              ErrorOverlay: CustomNoRowsTableOverlay,
            }}
            componentsProps={{
              noRowsOverlay: { pageType: BID_LINES_PAGE_TYPE },
              // errorOverlay: {
              //   errorMessage: props.errorObject.message,
              //   errorCode: props.errorObject.code,
              // },
              // toolbar: { station: props.station },
            }}
            loading={props.isBidLinesLoading}
            disableColumnMenu={true}
            showColumnVerticalBorder={true}
            showCellVerticalBorder={true}
            initialState={{
              columns: {
                columnVisibilityModel: getUnionContractGivenWorkgroup(props.bid.workgroup) !== NON_CONTRACT && {
                  __detail_panel_toggle__: false,
                },
              },
              pinnedColumns:
                getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT
                  ? {
                      left: ["__detail_panel_toggle__", "lineNumber", "ftpt"],
                      right: ["shiftDiff", "blockLine"],
                    }
                  : {
                      left: ["lineNumber", "ftpt"],
                      right: ["shiftDiff", "blockLine"],
                    },
            }}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
          />
        </Box>
      </Grid>
      <ConfirmationDialog
        isDialogOpen={isBidLinesClearAllDialogOpen}
        title="Clear All Bid Lines"
        message="You are clearing all the bid lines. This cannot be undone."
        handleNoButtonClicked={() => setIsBidLinesClearAllDialogOpen(false)}
        handleYesButtonClicked={handleBidLinesClearAll}
      />
      <input id="loadCsvInput" hidden accept="txt/*" ref={inputRef} type="file" onChange={handleBidFileUploaded} />
    </Grid>
  );
};

CreateBidLines.propTypes = {
  bid: PropTypes.object.isRequired,
  bidLines: PropTypes.array.isRequired,
  bidGroup: PropTypes.array.isRequired,
  isBidLinesLoading: PropTypes.bool.isRequired,
  setBidLines: PropTypes.func.isRequired,
  schedDates: PropTypes.array.isRequired,
  bidName: PropTypes.string,
};

export default CreateBidLines;
